import { createSelector } from '@reduxjs/toolkit';
import {
  DigitalSection,
  DRTActionTypes,
  SurveyDataState,
  DigitalCase,
  RootState,
  DigitalQuestion,
  DigitalSubmissionMeta,
  DigitalGroundTruth,
  DigitalSubmission,
  QuestionCaseSetting,
  ADD_DIGITAL_SECTION,
  SET_DIGITAL_CASES,
  SET_DIGITAL_SECTIONS,
  SET_DIGITAL_SURVEY,
  SET_SELECTED_DIGITAL_SECTION,
  DELETE_DIGITAL_SECTION,
  SET_SELECTED_CASE,
  SET_DIGITAL_QUESTIONS,
  ADD_UPDATE_DIGITAL_GROUNDTRUTH,
  SET_DIGITAL_GROUNDTRUTHS,
  DELETE_DIGITAL_GROUNDTRUTH,
  ADD_DIGITAL_CASE,
  DELETE_DIGITAL_CASE,
  UPDATE_DIGITAL_CASE,
  UPDATE_DIGITAL_SECTION,
  SET_DIGITAL_SUBMISSIONS,
  ADD_UPDATE_DIGITAL_SUBMISSION,
  SET_DIGITAL_SUBMISSION_META,
  UPDATE_DIGITAL_SUBMISSION_META,
  SET_SELECTED_DIGITAL_SUBMISSION_META,
  SET_SURVEY_SUBMISSION_META,
  ADD_QUESTION_CASE_SETTING,
  SET_QUESTION_CASE_SETTING,
  UPDATE_QUESTION_CASE_SETTING,
  FullSubmissionMap,
  SET_DIGITAL_GROUND_TRUTH_GROUP,
  DigitalGroundTruthGroup,
  ADD_DIGITAL_GROUND_TRUTH_GROUP,
  DELETE_DIGITAL_GROUND_TRUTH_GROUP,
  SET_SELECTED_DIGITAL_GROUND_TRUTH_GROUP,
  SET_DIGITAL_SECTION_SCORE,
  DigitalSectionScore,
  SET_DIGITAL_SURVEY_SCORE,
  DigitalSurveyScore,
  CLEAR_DIGITAL_SECTION_SCORE,
  CLEAR_DIGITAL_SURVEY_SCORE,
  SET_DIGITAL_QUESTION_SCORE,
  CLEAR_DIGITAL_QUESTION_SCORE,
  DigitalQuestionScore,
  RESET_DIGITAL_SURVEY,
  // UPDATE_DIGITAL_GROUND_TRUTH_GROUP,
} from '../types';

const initialState: SurveyDataState = {
  digitalSections: {
    allIds: [],
    byId: {},
  },
  digitalCases: {
    allIds: [],
    byId: {},
  },
  digitalQuestions: {
    allIds: [],
    byId: {},
  },
  digitalGroundTruth: {
    allIds: [],
    byId: {},
  },
  digitalSubmission: {
    allIds: [],
    byId: {},
  },
  digitalSubmissionMeta: {
    allIds: [],
    byId: {},
  },
  questionCaseSetting: {
    allIds: [],
    byId: {},
  },
  digitalGroundTruthGroup: {
    allIds: [],
    byId: {},
    selectedGroundTruthGroup: null,
  },
  digitalSectionScore: {
    allIds: undefined,
    byId: undefined,
  },
  digitalSurveyScore: {
    allIds: undefined,
    byId: undefined,
  },
  digitalQuestionScore: {
    allIds: undefined,
    byId: undefined,
  },
};

function addSection(state: SurveyDataState, section: DigitalSection): SurveyDataState {
  return {
    ...state,
    digitalSections: {
      ...state.digitalSections,
      byId: {
        ...state.digitalSections.byId,
        [section.id]: section,
      },
      allIds: state.digitalSections.allIds.concat(section.id),
    },
  } as SurveyDataState;
}

function setDigitalSections(state: SurveyDataState, sections: DigitalSection[]): SurveyDataState {
  return {
    ...state,
    digitalSections: {
      ...state.digitalSections,
      allIds: sections.sort((a, b) => a.sortIndex - b.sortIndex).map((sec) => sec.id),
      byId: sections.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalSection }),
    },
  } as SurveyDataState;
}

function setDigitalCases(state: SurveyDataState, cases: DigitalCase[]): SurveyDataState {
  return {
    ...state,
    digitalCases: {
      ...state.digitalCases,
      allIds: cases.sort((a, b) => a.sortIndex - b.sortIndex).map((c) => c.id),
      byId: cases.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalCase }),
    },
  } as SurveyDataState;
}

function addDigitalCase(state: SurveyDataState, payload: DigitalCase): SurveyDataState {
  return {
    ...state,
    digitalCases: {
      ...state.digitalCases,
      allIds: state.digitalCases.allIds.concat(payload.id),
      byId: {
        ...state.digitalCases.byId,
        [payload.id]: payload,
      },
    },
  };
}

function updateDigitalCase(state: SurveyDataState, dcase: DigitalCase): SurveyDataState {
  return {
    ...state,
    digitalCases: {
      ...state.digitalCases,
      byId: {
        ...state.digitalCases.byId,
        [dcase.id]: dcase,
      },
    },
  };
}

function deleteDigitalCase(state: SurveyDataState, caseId: number): SurveyDataState {
  const { [caseId]: del, ...rest } = state.digitalCases.byId;
  return {
    ...state,
    digitalCases: {
      ...state.digitalCases,
      allIds: state.digitalCases.allIds.filter((id) => id !== caseId),
      byId: rest,
    },
  };
}

function setDigitalQuestions(
  state: SurveyDataState, questions: DigitalQuestion[],
): SurveyDataState {
  return {
    ...state,
    digitalQuestions: {
      ...state.digitalQuestions,
      allIds: questions.sort((a, b) => a.sortIndex - b.sortIndex).map((q) => q.id),
      byId: questions.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalQuestion }),
    },
  } as SurveyDataState;
}

function deleteSection(state: SurveyDataState, sectionId: number): SurveyDataState {
  const { [sectionId]: del, ...rest } = state.digitalSections.byId;
  return {
    ...state,
    digitalSections: {
      ...state.digitalSections,
      allIds: state.digitalSections.allIds.filter((id) => id !== sectionId),
      byId: rest,
    },
  };
}

function updateSection(state: SurveyDataState, payload: DigitalSection): SurveyDataState {
  return {
    ...state,
    digitalSections: {
      ...state.digitalSections,
      byId: {
        ...state.digitalSections.byId,
        [payload.id]: payload,
      },
    },
  };
}

function addOrUpdateDigitalGroundTruth(
  state: SurveyDataState, gt: DigitalGroundTruth,
): SurveyDataState {
  const newState: SurveyDataState = {
    ...state,
    digitalGroundTruth: {
      ...state.digitalGroundTruth,
      byId: {
        ...state.digitalGroundTruth.byId,
        [gt.id]: gt,
      },
    },
  };
  const existingRecord = state.digitalGroundTruth.byId[gt.id];
  if (!existingRecord) {
    newState.digitalGroundTruth.allIds = newState.digitalGroundTruth.allIds.concat(gt.id);
  }
  return newState;
}

function setDigitalGroundTruths(
  state: SurveyDataState, gts: DigitalGroundTruth[],
): SurveyDataState {
  return {
    ...state,
    digitalGroundTruth: {
      ...state.digitalGroundTruth,
      byId: gts.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, DigitalGroundTruth>),
      allIds: gts.map((g) => g.id),
    },
  };
}

function deleteDigitalGroundTruth(state: SurveyDataState, gtId: number): SurveyDataState {
  const { [gtId]: del, ...rest } = state.digitalGroundTruth.byId;
  return {
    ...state,
    digitalGroundTruth: {
      ...state.digitalGroundTruth,
      allIds: state.digitalGroundTruth.allIds.filter((id) => id !== gtId),
      byId: rest,
    },
  };
}

function setDigitalSubmissions(
  state: SurveyDataState, submissions: DigitalSubmission[],
): SurveyDataState {
  return {
    ...state,
    digitalSubmission: {
      ...state.digitalSubmission,
      allIds: submissions.map((s) => s.id),
      byId: submissions.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalSubmission }),
    },
  } as SurveyDataState;
}

function addOrUpdateDigitalSubmission(
  state: SurveyDataState, submission: DigitalSubmission,
): SurveyDataState {
  const newState: SurveyDataState = {
    ...state,
    digitalSubmission: {
      ...state.digitalSubmission,
      byId: {
        ...state.digitalSubmission.byId,
        [submission.id]: submission,
      },
    },
  };
  const existingRecord = state.digitalSubmission.byId[submission.id];
  if (!existingRecord) {
    newState.digitalSubmission.allIds = newState.digitalSubmission.allIds.concat(submission.id);
  }
  return newState;
}

function setDigitalSubmissionMetas(
  state: SurveyDataState, metas: DigitalSubmissionMeta[],
): SurveyDataState {
  return {
    ...state,
    digitalSubmissionMeta: {
      ...state.digitalSubmissionMeta,
      allIds: metas.map((s) => s.id),
      byId: metas.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalSubmissionMeta }),
    },
  } as SurveyDataState;
}

function updateDigitalSubmissionMeta(
  state: SurveyDataState, meta: DigitalSubmissionMeta,
): SurveyDataState {
  return {
    ...state,
    digitalSubmissionMeta: {
      ...state.digitalSubmissionMeta,
      byId: {
        ...state.digitalSubmissionMeta.byId,
        [meta.id]: meta,
      },
    },
  };
}

function addQuestionCaseSetting(state: SurveyDataState, qcs: QuestionCaseSetting): SurveyDataState {
  return {
    ...state,
    questionCaseSetting: {
      ...state.questionCaseSetting,
      byId: {
        ...state.questionCaseSetting.byId,
        [qcs.id]: qcs,
      },
      allIds: state.questionCaseSetting.allIds.concat(qcs.id),
    },
  };
}

function setQuestionCaseSettings(
  state: SurveyDataState, qcs: QuestionCaseSetting[],
): SurveyDataState {
  return {
    ...state,
    questionCaseSetting: {
      ...state.questionCaseSetting,
      byId: qcs.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, QuestionCaseSetting>),
      allIds: qcs.map((s) => s.id),
    },
  };
}

function updateQuestionCaseSetting(
  state: SurveyDataState, qcs: QuestionCaseSetting,
): SurveyDataState {
  return {
    ...state,
    questionCaseSetting: {
      ...state.questionCaseSetting,
      byId: {
        ...state.questionCaseSetting.byId,
        [qcs.id]: qcs,
      },
    },
  };
}

function setDigitalGroundTruthGroups(
  state: SurveyDataState, gtGroups: DigitalGroundTruthGroup[],
): SurveyDataState {
  return {
    ...state,
    digitalGroundTruthGroup: {
      ...state.digitalGroundTruthGroup,
      allIds: gtGroups.map((g) => g.id),
      byId: gtGroups.reduce((prev, current) => ({
        ...prev,
        [current.id]: current,
      }), {} as { [key: string]: DigitalGroundTruthGroup }),
    },
  } as SurveyDataState;
}

function addDigitalGroundTruthGroup(
  state: SurveyDataState, gtGroup: DigitalGroundTruthGroup,
): SurveyDataState {
  return {
    ...state,
    digitalGroundTruthGroup: {
      ...state.digitalGroundTruthGroup,
      byId: {
        ...state.digitalGroundTruthGroup.byId,
        [gtGroup.id]: gtGroup,
      },
      allIds: state.digitalGroundTruthGroup.allIds.concat(gtGroup.id),
    },
  } as SurveyDataState;
}

function deleteDigitalGroundTruthGroup(state: SurveyDataState, gtGroupId: number): SurveyDataState {
  const { [gtGroupId]: del, ...rest } = state.digitalGroundTruthGroup.byId;
  return {
    ...state,
    digitalGroundTruthGroup: {
      ...state.digitalGroundTruthGroup,
      allIds: state.digitalGroundTruthGroup.allIds.filter((id) => id !== gtGroupId),
      byId: rest,
    },
  };
}

// function updateDigitalGroundTruthGroup(
//   state: SurveyDataState, dgtg: DigitalGroundTruthGroup,
// ): SurveyDataState {
//   return {
//     ...state,
//     digitalGroundTruthGroup: {
//       ...state.digitalGroundTruthGroup,
//       byId: {
//         ...state.digitalGroundTruthGroup.byId,
//         [dgtg.id]: dgtg,
//       },
//     },
//   };
// }

function setDigitalSectionScore(
  state: SurveyDataState, dss: DigitalSectionScore[],
): SurveyDataState {
  return {
    ...state,
    digitalSectionScore: {
      ...state.digitalSectionScore,
      byId: dss.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, DigitalSectionScore>),
      allIds: dss.map((s) => s.id),
    },
  };
}

function setDigitalSurveyScore(
  state: SurveyDataState, dss: DigitalSurveyScore[],
): SurveyDataState {
  return {
    ...state,
    digitalSurveyScore: {
      ...state.digitalSurveyScore,
      byId: dss.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, DigitalSurveyScore>),
      allIds: dss.map((s) => s.id),
    },
  };
}

function setDigitalQuestionScore(
  state: SurveyDataState, dss: DigitalQuestionScore[],
): SurveyDataState {
  return {
    ...state,
    digitalQuestionScore: {
      ...state.digitalQuestionScore,
      byId: dss.reduce((acc, current) => ({
        ...acc,
        [current.id]: current,
      }), {} as Record<number, DigitalQuestionScore>),
      allIds: dss.map((s) => s.id),
    },
  };
}

function clearScores(state: SurveyDataState, key: 'digitalSectionScore'|'digitalSurveyScore'|'digitalQuestionScore'): SurveyDataState {
  return {
    ...state,
    [key]: {
      allIds: undefined,
      byId: undefined,
    },
  };
}

/**
 * DRT Reducer function
 */
function DRTReducer(state = initialState, action: DRTActionTypes): SurveyDataState {
  switch (action.type) {
    case RESET_DIGITAL_SURVEY:
      return initialState;
    case SET_DIGITAL_SURVEY:
      return {
        ...state,
        survey: action.payload,
      };
    case ADD_DIGITAL_SECTION:
      return addSection(state, action.payload);
    case SET_DIGITAL_SECTIONS:
      return setDigitalSections(state, action.payload);
    case SET_SELECTED_DIGITAL_SECTION:
      return {
        ...state,
        digitalSections: {
          ...state.digitalSections,
          selectedSection: action.payload,
        },
      };
    case DELETE_DIGITAL_SECTION:
      return deleteSection(state, action.payload);
    case UPDATE_DIGITAL_SECTION:
      return updateSection(state, action.payload);
    case SET_DIGITAL_CASES:
      return setDigitalCases(state, action.payload);
    case SET_SELECTED_CASE:
      return {
        ...state,
        digitalCases: {
          ...state.digitalCases,
          selectedCase: action.payload,
        },
      };
    case ADD_DIGITAL_CASE:
      return addDigitalCase(state, action.payload);
    case UPDATE_DIGITAL_CASE:
      return updateDigitalCase(state, action.payload);
    case DELETE_DIGITAL_CASE:
      return deleteDigitalCase(state, action.payload);
    case SET_DIGITAL_QUESTIONS:
      return setDigitalQuestions(state, action.payload);
    case ADD_UPDATE_DIGITAL_GROUNDTRUTH:
      return addOrUpdateDigitalGroundTruth(state, action.payload);
    case SET_DIGITAL_GROUNDTRUTHS:
      return setDigitalGroundTruths(state, action.payload);
    case DELETE_DIGITAL_GROUNDTRUTH:
      return deleteDigitalGroundTruth(state, action.payload);
    case SET_DIGITAL_SUBMISSIONS:
      return setDigitalSubmissions(state, action.payload);
    case ADD_UPDATE_DIGITAL_SUBMISSION:
      return addOrUpdateDigitalSubmission(state, action.payload);
    case SET_DIGITAL_SUBMISSION_META:
      return setDigitalSubmissionMetas(state, action.payload);
    case UPDATE_DIGITAL_SUBMISSION_META:
      return updateDigitalSubmissionMeta(state, action.payload);
    case ADD_QUESTION_CASE_SETTING:
      return addQuestionCaseSetting(state, action.payload);
    case SET_QUESTION_CASE_SETTING:
      return setQuestionCaseSettings(state, action.payload);
    case UPDATE_QUESTION_CASE_SETTING:
      return updateQuestionCaseSetting(state, action.payload);
    case SET_SELECTED_DIGITAL_SUBMISSION_META:
      return {
        ...state,
        digitalSubmissionMeta: {
          ...state.digitalSubmissionMeta,
          selectedSubmissionMeta: action.payload,
        },
      };
    case SET_SURVEY_SUBMISSION_META:
      return {
        ...state,
        surveySubmissionMeta: action.payload,
      };
    case SET_DIGITAL_GROUND_TRUTH_GROUP:
      return setDigitalGroundTruthGroups(state, action.payload);
    case ADD_DIGITAL_GROUND_TRUTH_GROUP:
      return addDigitalGroundTruthGroup(state, action.payload);
    // case UPDATE_DIGITAL_GROUND_TRUTH_GROUP:
    //   return updateDigitalGroundTruthGroup(state, action.payload);
    case DELETE_DIGITAL_GROUND_TRUTH_GROUP:
      return deleteDigitalGroundTruthGroup(state, action.payload);
    case SET_SELECTED_DIGITAL_GROUND_TRUTH_GROUP:
      return {
        ...state,
        digitalGroundTruthGroup: {
          ...state.digitalGroundTruthGroup,
          selectedGroundTruthGroup: action.payload,
        },
      };
    case SET_DIGITAL_SECTION_SCORE:
      return setDigitalSectionScore(state, action.payload);
    case CLEAR_DIGITAL_SECTION_SCORE:
      return clearScores(state, 'digitalSectionScore');
    case SET_DIGITAL_SURVEY_SCORE:
      return setDigitalSurveyScore(state, action.payload);
    case CLEAR_DIGITAL_SURVEY_SCORE:
      return clearScores(state, 'digitalSurveyScore');
    case SET_DIGITAL_QUESTION_SCORE:
      return setDigitalQuestionScore(state, action.payload);
    case CLEAR_DIGITAL_QUESTION_SCORE:
      return clearScores(state, 'digitalQuestionScore');
    default:
      return state;
  }
}

export default DRTReducer;

/** DRT Selectors */

const selectDRTSectionsAllIds = (state: RootState) => state.SurveyData.digitalSections.allIds;
const selectDRTSectionsByIds = (state: RootState) => state.SurveyData.digitalSections.byId;
const selectSelectedSectionId = (state: RootState) => state
  .SurveyData.digitalSections.selectedSection;
const selectDRTCasesAllIds = (state: RootState) => state.SurveyData.digitalCases.allIds;
const selectDRTCasesById = (state: RootState) => state.SurveyData.digitalCases.byId;
const selectSelectedCaseId = (state: RootState) => state.SurveyData.digitalCases.selectedCase;
const selectDRTQuestionsAllIds = (state: RootState) => state.SurveyData.digitalQuestions.allIds;
const selectDRTQuestionsByIds = (state: RootState) => state.SurveyData.digitalQuestions.byId;
const selectDRTGroundTruthById = (state: RootState) => state.SurveyData.digitalGroundTruth.byId;
const selectDGTGroupsAllIds = (state: RootState) => state.SurveyData.digitalGroundTruthGroup.allIds;
const selectDGTGroupsById = (state: RootState) => state.SurveyData.digitalGroundTruthGroup.byId;
export const selectSelectedGroundTruthGroupId = (state: RootState) => state
  .SurveyData.digitalGroundTruthGroup.selectedGroundTruthGroup;
const selectQuestionCaseSettingById = (
  state: RootState,
) => state.SurveyData.questionCaseSetting.byId;
const selectQuestionCaseSettingsAllIds = (
  state: RootState,
) => state.SurveyData.questionCaseSetting.allIds;
const selectDigitalSubmissionsAllIds = (state: RootState) => state
  .SurveyData.digitalSubmission.allIds;
const selectDigitalSubmissionsById = (state: RootState) => state.SurveyData.digitalSubmission.byId;
const selectDigitalSubmissionMetaById = (state: RootState) => state
  .SurveyData.digitalSubmissionMeta.byId;
const selectSelectedDigitalSubmissionMetaId = (
  state: RootState,
) => state.SurveyData.digitalSubmissionMeta.selectedSubmissionMeta;
const selectDigitalSectionScoreAllIds = (state: RootState) => state
  .SurveyData.digitalSectionScore.allIds;
const selectDigitalSectionScoreById = (state: RootState) => state
  .SurveyData.digitalSectionScore.byId;
const selectDigitalSurveyScoreAllIds = (state: RootState) => state
  .SurveyData.digitalSurveyScore.allIds;
const selectDigitalSurveyScoreById = (state: RootState) => state
  .SurveyData.digitalSurveyScore.byId;
const selectDigitalQuestionScoreAllIds = (state: RootState) => state
  .SurveyData.digitalQuestionScore.allIds;
const selectDigitalQuestionScoreById = (state: RootState) => state
  .SurveyData.digitalQuestionScore.byId;

export const selectDigitalSections = createSelector([
  selectDRTSectionsAllIds,
  selectDRTSectionsByIds,
], (ids, secsMap) => ids.map((id) => secsMap[id]));

export const selectDigitalCases = createSelector([
  selectDRTCasesAllIds,
  selectDRTCasesById,
  selectSelectedSectionId,
], (ids, byIds, selectedSectionId) => ids
  .map((id) => byIds[id])
  .filter((drtCase) => drtCase.digitalSectionId === selectedSectionId));

export const selectAllDigitalCases = createSelector([
  selectDRTCasesAllIds,
  selectDRTCasesById,
], (ids, byIds) => ids
  .map((id) => byIds[id]));

export const selectSelectedCase = createSelector([
  selectDRTCasesById,
  selectSelectedCaseId,
], (casesById, selectedCaseId) => (selectedCaseId ? casesById[selectedCaseId] : undefined));

export const selectDigitalQuestions = createSelector([
  selectDRTQuestionsAllIds,
  selectDRTQuestionsByIds,
], (ids, questMap) => ids.map((id) => questMap[id]));

export const selectDigitalQuestionsBySection = createSelector([
  selectDigitalQuestions,
  selectSelectedSectionId,
], (
  allQuestions,
  secId,
) => allQuestions.filter((q) => secId && q.digitalSectionId === secId && !q.isContextQuestion));

export const selectContextQuestionBySection = createSelector([
  selectDigitalQuestions,
  selectSelectedSectionId,
], (
  allQuestions,
  secId,
) => allQuestions.filter((q) => secId && q.digitalSectionId === secId && q.isContextQuestion));

export const selectNormalQuestionsDRT = createSelector([
  selectDRTQuestionsAllIds,
  selectDRTQuestionsByIds,
], (ids, questMap) => ids.map((id) => questMap[id]).filter((q) => !q.isContextQuestion));

export const selectContextQuestionsDRT = createSelector([
  selectDRTQuestionsAllIds,
  selectDRTQuestionsByIds,
], (ids, questMap) => ids.map((id) => questMap[id]).filter((q) => q.isContextQuestion));

const selectSectionId = (state: RootState, sectionId: number | undefined) => sectionId;

/**
 * GroundTruth by sections' dictionary<questionId, caseId>: DigitalGroundTruth
 * used in SurveyV2 only
 */
export const selectDigitalGTDictionaryByQuestionAndCase = createSelector([
  selectDRTGroundTruthById,
  selectSectionId,
  selectSelectedGroundTruthGroupId,
], (dgtMap, sectionId, gtGroupId) => {
  let dgtListBySections = Object.values(dgtMap)
    .filter((dgt) => (dgt.digitalSectionId === sectionId));
  if (gtGroupId) {
    dgtListBySections = dgtListBySections.filter(
      (dgt) => dgt.digitalGroundTruthGroupId === gtGroupId,
    );
  }
  const gtDictionary: Record<number, Record<number, DigitalGroundTruth> | undefined> = {};
  dgtListBySections.forEach((dgt) => {
    gtDictionary[dgt.digitalQuestionId] = {
      ...gtDictionary[dgt.digitalQuestionId],
      [dgt.digitalCaseId]: dgt,
    };
  });
  const dictProto = {
    get: (qId: number, cId: number) => gtDictionary[qId]?.[cId],
  };
  const wrappedDict: typeof dictProto = Object.create(dictProto);
  Object.assign(wrappedDict, gtDictionary);
  return wrappedDict as typeof dictProto & typeof gtDictionary;
});

/**
 * GroundTruth by sections' dictionary<questionId, caseId>: DigitalGroundTruth
 * used in DRT only
 */
export const selectDigitalGTDictionaryByQuestionAndCaseDRT = createSelector([
  selectDRTGroundTruthById,
  selectSectionId,
], (dgtMap, sectionId) => {
  const dgtListBySections = Object.values(dgtMap)
    .filter((dgt) => (
      dgt.digitalSectionId === sectionId
    ));
  const gtDictionary: Record<number, Record<number, DigitalGroundTruth> | undefined> = {};
  dgtListBySections.forEach((dgt) => {
    gtDictionary[dgt.digitalQuestionId] = {
      ...gtDictionary[dgt.digitalQuestionId],
      [dgt.digitalCaseId]: dgt,
    };
  });
  const dictProto = {
    get: (qId: number, cId: number) => gtDictionary[qId]?.[cId],
  };
  const wrappedDict: typeof dictProto = Object.create(dictProto);
  Object.assign(wrappedDict, gtDictionary);
  return wrappedDict as typeof dictProto & typeof gtDictionary;
});

/** valid for SurveyV2 only. not for DRT */
export const selectGroundTruthsPerSectionAndGroup = createSelector([
  selectDRTGroundTruthById,
  selectSelectedSectionId,
  selectSelectedGroundTruthGroupId,
], (gtMap, sectionId, gtGroupId) => {
  const dgtListBySectionsAndGroup = Object.values(gtMap)
    .filter((dgt) => (
      dgt.digitalSectionId === sectionId
      && (dgt.digitalGroundTruthGroupId === gtGroupId && !!dgt.digitalGroundTruthGroupId)
    ));
  return dgtListBySectionsAndGroup;
});

export const selectGroundTruthsPerSection = createSelector([
  selectDRTGroundTruthById,
  selectSelectedSectionId,
], (gtMap, sectionId) => {
  const dgtListBySectionsAndGroup = Object.values(gtMap)
    .filter((dgt) => (dgt.digitalSectionId === sectionId));
  return dgtListBySectionsAndGroup;
});

export const selectSelectedSection = createSelector([
  selectDRTSectionsByIds,
  selectSelectedSectionId,
], (sectionsMap, selectedId): DigitalSection | undefined => (
  selectedId ? sectionsMap[selectedId] : undefined));

export const selectDigitalSubmissions = createSelector([
  selectSelectedSectionId,
  selectDigitalSubmissionsById,
], (
  secId, submissionsMap,
) => Object.values(submissionsMap).filter((s) => s.digitalSectionId === secId));

export const selectDigitalSubmissionByQuestionAndCase = createSelector([
  selectSelectedSectionId,
  selectDigitalSubmissionsById,
], (secId, submissionsMap) => {
  const submissionsBySection = Object.values(submissionsMap)
    .filter((s) => s.digitalSectionId === secId);
  const sDictionary: Record<number, Record<number, DigitalSubmission> | undefined> = {};
  submissionsBySection.forEach((ds) => {
    if (ds.digitalCaseId !== undefined) {
      sDictionary[ds.digitalQuestionId] = {
        ...sDictionary[ds.digitalQuestionId],
        [ds.digitalCaseId]: ds,
      };
    }
  });
  return sDictionary;
});

/**
 * selected selected section's context question map by questionId
 * @returns Map<QuestionId, DigitalSubmission>
 */
export const selectContextSubmissionByQuestion = createSelector([
  selectSelectedSectionId,
  selectDigitalSubmissionsById,
], (secId, submissionsMap) => {
  const ctxSubmissionsBySection = Object.values(submissionsMap)
    .filter((s) => s.digitalSectionId === secId && !s.digitalCaseId);
  return ctxSubmissionsBySection.reduce((acc, cur) => {
    acc[cur.digitalQuestionId] = cur;
    return acc;
  }, {} as Record<number, DigitalSubmission | undefined>);
});

export const selectEmptySubmissionCount = createSelector([
  selectDRTQuestionsAllIds,
  selectDigitalCases,
  selectDigitalSubmissions,
], (qIds, cases, sIds) => {
  const emptyCount = qIds.length * cases.length - sIds.length;
  return emptyCount;
});

export const selectSelectedSubmissionMeta = createSelector([
  selectDigitalSubmissionMetaById,
  selectSelectedDigitalSubmissionMetaId,
], (metaMap, selectedId): DigitalSubmissionMeta | undefined => (
  selectedId ? metaMap[selectedId] : undefined));

export const selectDigitalSubmissionMetaBySection = createSelector([
  selectDigitalSubmissionMetaById,
  selectSelectedSectionId,
], (metaMap, sectionId) => (
  Object.values(metaMap).find((meta) => meta.selectedSectionId === sectionId)
));

export const selectAllDigitalSubmissionMeta = createSelector([
  selectDigitalSubmissionMetaById,
], (metaMap) => Object.values(metaMap));

export const selectSettingsDictionaryByQuestionAndCase = createSelector([
  selectQuestionCaseSettingById,
  selectSelectedSectionId,
], (qcsMap, sectionId) => {
  const qcsListBySections = Object.values(qcsMap)
    .filter((qcs) => qcs.digitalSectionId === sectionId && !!qcs.digitalCaseId);
  const qcsDictionary: Record<number, Record<number, QuestionCaseSetting> | undefined> = {};
  qcsListBySections.forEach((qcs) => {
    qcsDictionary[qcs.digitalQuestionId] = {
      ...qcsDictionary[qcs.digitalQuestionId],
      [qcs.digitalCaseId]: qcs,
    };
  });
  const dictProto = {
    get: (qId: number, cId: number) => qcsDictionary[qId]?.[cId],
  };
  const wrappedDict: typeof dictProto = Object.create(dictProto);
  Object.assign(wrappedDict, qcsDictionary);
  return wrappedDict as typeof dictProto & typeof qcsDictionary;
});

export const selectContextQuestionSettingsByQuestionId = createSelector([
  selectQuestionCaseSettingById,
  selectSelectedSectionId,
], (qcsMap, sectionId) => {
  const qcsListBySections = Object.values(qcsMap)
    .filter((qcs) => qcs.digitalSectionId === sectionId && !qcs.digitalCaseId);
  const contextQuestionSettingMap = qcsListBySections.reduce((acc, cur) => {
    acc[cur.digitalQuestionId] = cur;
    return acc;
  }, {} as Record<number, QuestionCaseSetting>);
  return contextQuestionSettingMap;
});

export const selectQuestionCaseSettings = createSelector([
  selectQuestionCaseSettingsAllIds,
  selectQuestionCaseSettingById,
], (ids, byId) => ids.map((id) => byId[id]));

export const selectSubmissionBySectionQuestionAndCase = createSelector([
  selectDigitalSubmissionsById,
], (submissionsMap) => {
  const submissions = Object.values(submissionsMap)
    .filter((s) => !!s.digitalCaseId);
  return submissions.reduce((acc, cur) => {
    acc[cur.digitalSectionId] = {
      ...acc[cur.digitalSectionId],
      [cur.digitalQuestionId]: {
        ...acc[cur.digitalSectionId]?.[cur.digitalQuestionId],
        [cur.digitalCaseId as number]: cur,
      },
    };
    return acc;
  }, {} as FullSubmissionMap);
});

export const selectCtxSubmissionBySectionQuestion = createSelector([
  selectDigitalSubmissionsById,
], (
  submissionsMap,
) => {
  const ctxSubmissions = Object.values(submissionsMap)
    .filter((s) => !s.digitalCaseId);
  return ctxSubmissions.reduce((acc, cur) => {
    acc[cur.digitalSectionId] = {
      ...acc[cur.digitalSectionId],
      [cur.digitalQuestionId]: cur,
    };
    return acc;
  }, {} as Record<number, Record<number, DigitalSubmission>>);
});

/** GroundTruthGroups selectors */

export const selectDigitalGTGroups = createSelector([
  selectDGTGroupsAllIds,
  selectDGTGroupsById,
], (ids, gMap) => ids.map((id) => gMap[id]));

export const selectSelectedGroundTruthGroup = createSelector([
  selectDGTGroupsById,
  selectSelectedGroundTruthGroupId,
], (gtGroupById, selectedGtGroup) => (selectedGtGroup ? gtGroupById[selectedGtGroup] : null));

export const selectDigitalSectionScores = createSelector([
  selectDigitalSectionScoreAllIds,
  selectDigitalSectionScoreById,
], (ids, scoreMap) => (ids && scoreMap ? ids.map((i) => scoreMap[i]) : undefined));

export const selectDigitalSurveyScores = createSelector([
  selectDigitalSurveyScoreAllIds,
  selectDigitalSurveyScoreById,
], (ids, scoreMap) => (ids && scoreMap ? ids.map((i) => scoreMap[i]) : undefined));

export const selectDigitalQuestionScores = createSelector([
  selectDigitalQuestionScoreAllIds,
  selectDigitalQuestionScoreById,
], (ids, scoreMap) => (ids && scoreMap ? ids.map((i) => scoreMap[i]) : undefined));

export const selectSectionScoreBasedOnSectionAndGTGroupId = createSelector([
  selectDigitalSectionScores,
  selectSelectedSectionId,
  selectSelectedGroundTruthGroupId,
], (sectionScores, sectionId, gtId) => sectionScores?.find(
  (s) => s.digitalSectionId === sectionId && s.digitalGroundTruthGroupId === gtId,
));

export const selectDigitalSurveyScoreBasedOnGTGroupId = createSelector([
  selectDigitalSurveyScores,
  selectSelectedGroundTruthGroupId,
], (sectionScores, gtId) => sectionScores?.find((s) => s.digitalGroundTruthGroupId === gtId));
